import { useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Cloud, Shield } from 'lucide-react';
import { TOOLS_BY_SLUG } from '../data';
import { iconFor } from '../icons';
import { isFramedOnMarketingHost, sizeBucket } from '../runners/pdf-to-link-support';
import {
  trackToolShareHandoffClicked,
  trackToolShareHandoffShown,
  trackToolSuccessPanelShown,
  trackToolSuccessNextToolClicked,
} from '@/lib/posthog';
import { formatShareSize, shareLinkHandoffHref, stashShareHandoff } from './share-handoff';
import { useToolShellContext, type OfferedShareablePdf } from './tool-shell-context';
import {
  COPY_BY_VARIANT,
  resolveToolSuccessCopy,
  type ToolSuccessVariant,
} from './tool-success-copy';
import { getToolDetailHref, type ToolSurfaceConfig } from './tool-surface-config';
import { useTranslation } from '@/i18n';
import { localizeTool } from '@/i18n/localized-tool';

import { Button } from '@/components/ui';

/** `pdf-to-json` → `pdfToJson`, matching the `tools.catalog.*` key shape. */
function successKey(slug: string): string {
  return slug.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

/**
 * Inline, per-tool success state for /tools/* (W.4.51).
 *
 * Replaces SuccessStudioModal — a popup that covered the result 800ms after
 * the job finished and offered Studio as its only exit — and the Studio-linked
 * ToolSuccessInlineCta banner that sat under it. This panel is part of the
 * page, not on top of it:
 *
 *   1. It confirms what the tool produced.
 *   2. It says where the output went.
 *   3. It points at sibling tools. Nothing here leaves /tools/*.
 *   4. The one cross-tool file handoff is Get a share link → /tools/pdf-to-link
 *      (W.4.124); the account gate happens there, at the value moment (#2718).
 *      It is a button, so nothing here can open an empty new tab.
 *
 * It renders for every visitor (signed in or out) with no dismissal window,
 * because it is a result, not an upsell — there is nothing to suppress.
 *
 * Copy goes through i18n with English defaults, so a locale that has no
 * `tools.success.*` entries yet falls back to English — the same behavior the
 * (English-only) modal had. Adding a translation is then a data-only change.
 */
export function ToolSuccessPanel() {
  const ctx = useToolShellContext();
  if (!ctx || !ctx.surfaceConfig.successPanel) return null;
  if (!ctx.hasSucceeded) return null;
  // W.4.124: the runner's published state is the result; a second card repeated
  // it with pre-share next steps and a false deletion notice.
  if (ctx.toolSlug === 'pdf-to-link') return null;
  return (
    <SuccessPanelBody
      toolSlug={ctx.toolSlug}
      surfaceConfig={ctx.surfaceConfig}
      variant={ctx.successVariant}
      shareablePdf={ctx.shareablePdf}
    />
  );
}

function SuccessPanelBody({
  toolSlug,
  surfaceConfig,
  variant,
  shareablePdf,
}: {
  toolSlug: string;
  surfaceConfig: ToolSurfaceConfig;
  variant?: ToolSuccessVariant;
  shareablePdf: OfferedShareablePdf | null;
}) {
  const { t } = useTranslation();
  const copy = variant ? COPY_BY_VARIANT[variant] : resolveToolSuccessCopy(toolSlug);
  const tool = TOOLS_BY_SLUG[toolSlug];
  const isLocal = tool?.processingType !== 'cloud';
  const base = variant
    ? `tools.success.variants.${successKey(variant)}`
    : `tools.success.${successKey(toolSlug)}`;
  const title = t(`${base}.title`, { defaultValue: copy.title });
  const body = t(`${base}.body`, { defaultValue: copy.body });
  const shownRef = useRef<string | null>(null);

  useEffect(() => {
    if (shownRef.current === toolSlug) return;
    shownRef.current = toolSlug;
    trackToolSuccessPanelShown({ tool: toolSlug });
  }, [toolSlug]);

  const nextTools = copy.nextTools
    .filter((slug) => slug !== toolSlug)
    .map((slug) => TOOLS_BY_SLUG[slug])
    .filter((next): next is NonNullable<typeof next> => Boolean(next))
    // The rest of the page shows tool names in the active locale; these links
    // must match rather than reverting to the English registry name.
    .map((next) => localizeTool(next, t));

  return (
    <section
      aria-labelledby="tool-success-title"
      data-testid="tool-success-panel"
      className="mt-6 overflow-hidden rounded-xl border border-border bg-card shadow-card"
    >
      {/* Deliberately NOT a second green alert box: most runners already show
          a small "done" confirmation at the action button. This is the page's
          outcome + next steps, so it reads as a card, not a duplicate toast. */}
      <div className="flex items-start gap-3 px-5 py-4" role="status">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden="true" />
        <div className="min-w-0">
          <h2 id="tool-success-title" className="text-base font-semibold text-foreground">
            {title}
          </h2>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{body}</p>
        </div>
      </div>

      <div className="border-t border-border bg-muted/30 px-5 py-4">
        <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          {isLocal ? (
            <>
              <Shield className="h-3.5 w-3.5 text-success" aria-hidden="true" />
              {t('tools.success.localNotice', {
                defaultValue: 'Processed in your browser — the file never left your device.',
              })}
            </>
          ) : (
            <>
              <Cloud className="h-3.5 w-3.5 text-info" aria-hidden="true" />
              {t('tools.success.cloudNotice', {
                defaultValue:
                  'Processed in the cloud — your upload is deleted after the job finishes.',
              })}
            </>
          )}
        </p>

        {shareablePdf ? (
          <ShareHandoffSection
            toolSlug={toolSlug}
            offer={shareablePdf}
            surfaceConfig={surfaceConfig}
            variant={variant}
          />
        ) : null}

        {nextTools.length > 0 ? (
          <>
            <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('tools.success.next', { defaultValue: 'Next' })}
            </h3>
            <ul className="mt-2 grid gap-2 sm:grid-cols-3">
              {nextTools.map((next) => {
                const Icon = iconFor(next.iconName);
                return (
                  <li key={next.slug}>
                    <Link
                      to={getToolDetailHref(surfaceConfig, next.slug)}
                      onClick={() =>
                        trackToolSuccessNextToolClicked({
                          tool: toolSlug,
                          next_tool: next.slug,
                        })
                      }
                      className="group flex h-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm transition-colors hover:border-primary/40 hover:bg-muted"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                        {next.name}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}
      </div>
    </section>
  );
}

/**
 * 'Get a share link' (W.4.124, F7): the runner offered its single-PDF output
 * through offerShareLink, and this step carries it into PDF to Link without a
 * download and re-upload. It pitches the link, not an account.
 *
 * Not offered on a page another site frames (F10): PDF to Link cannot publish
 * there, and its own tab would not have the PDF.
 */
function ShareHandoffSection({
  toolSlug,
  offer,
  surfaceConfig,
  variant,
}: {
  toolSlug: string;
  offer: OfferedShareablePdf;
  surfaceConfig: ToolSurfaceConfig;
  variant?: ToolSuccessVariant;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleId = useId();
  const [framed] = useState(() => isFramedOnMarketingHost());
  const bucket = sizeBucket(offer.size);
  const eligible = offer.pdf !== null;
  // On a phone the PDF only lives on this page: leaving for Compress loses it.
  const onPageOnly = variant === 'pdf-ready-on-page';
  const shownRef = useRef<OfferedShareablePdf | null>(null);

  useEffect(() => {
    if (framed || shownRef.current === offer) return;
    shownRef.current = offer;
    trackToolShareHandoffShown({ tool: toolSlug, size_bucket: bucket, eligible });
  }, [framed, offer, toolSlug, bucket, eligible]);

  if (framed) return null;

  const getShareLink = () => {
    if (!offer.pdf) return;
    stashShareHandoff({ pdf: offer.pdf, fileName: offer.fileName, sourceTool: toolSlug });
    try {
      trackToolShareHandoffClicked({ tool: toolSlug, size_bucket: bucket });
    } catch {
      // Analytics must never strand the visitor on a dead button.
    }
    navigate(shareLinkHandoffHref(surfaceConfig.linkBase, toolSlug));
  };

  return (
    <section
      aria-labelledby={titleId}
      data-testid="tool-share-handoff"
      className="mt-4 rounded-lg border border-border bg-background px-4 py-3"
    >
      <h3 id={titleId} className="text-sm font-semibold text-foreground">
        {t('tools.success.shareHandoff.title', { defaultValue: 'Share it as a link' })}
      </h3>
      {offer.pdf ? (
        <>
          <p className="mt-1 text-sm leading-6 text-muted-foreground [overflow-wrap:anywhere]">
            {t('tools.success.shareHandoff.body', {
              defaultValue:
                'Turn {{fileName}} into a link anyone can open in a browser. It uploads when you create the link — no need to download and upload it again.',
              fileName: offer.fileName,
            })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
            <Button onClick={getShareLink} className="h-11 sm:h-9">
              {t('tools.success.shareHandoff.cta', { defaultValue: 'Get a share link' })}
              <ArrowRight aria-hidden="true" />
            </Button>
            <span className="text-xs text-muted-foreground">
              {t('tools.success.shareHandoff.note', { defaultValue: 'PDFs up to 5 MB' })}
            </span>
          </div>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {t('tools.success.shareHandoff.tooLarge', {
              defaultValue: 'This PDF is {{size}}. Share links take PDFs up to 5 MB.',
              size: formatShareSize(offer.size),
            })}
          </p>
          {toolSlug === 'compress' ? null : onPageOnly ? (
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {t('tools.success.shareHandoff.saveFirst', {
                defaultValue: 'Save it with Share or save above before you compress it.',
              })}
            </p>
          ) : (
            <Link
              to={getToolDetailHref(surfaceConfig, 'compress')}
              className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline sm:min-h-0"
            >
              {t('tools.success.shareHandoff.compressCta', { defaultValue: 'Compress PDF first' })}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </>
      )}
    </section>
  );
}
